import { useState, useMemo, Fragment, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, X } from 'lucide-react';
import { PlayDiagram } from '@/components/PlayDiagram';
import {
  buildGlossaryRegex,
  lookupGlossaryTerm,
  type GlossaryEntry,
} from '@/data/glossary';

/* ─── Inline tappable term ─────────────────────────────────── */

function GlossaryTerm({
  children,
  entry,
  onTap,
}: {
  children: ReactNode;
  entry: GlossaryEntry;
  onTap: (entry: GlossaryEntry) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onTap(entry)}
      title="In glossary — tap for definition"
      className="inline rounded px-0.5 -mx-0.5 bg-primary/10 hover:bg-primary/20 underline decoration-dotted decoration-primary underline-offset-2 text-primary font-semibold transition-colors cursor-pointer"
    >
      {children}
    </button>
  );
}

/* ─── Definition drawer (bottom sheet style) ───────────────── */

function GlossaryDrawer({
  entry,
  onClose,
}: {
  entry: GlossaryEntry | null;
  onClose: () => void;
}) {
  return (
    <AnimatePresence>
      {entry && (
        <>
          {/* Backdrop */}
          <motion.div
            key="glossary-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/50"
          />
          {/* Drawer */}
          <motion.div
            key="glossary-drawer"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-card border-t-2 border-border shadow-lg"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
            </div>

            <div className="px-5 pb-6 space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary flex-shrink-0" />
                  <h3 className="font-display font-extrabold text-lg capitalize text-foreground">
                    {entry.term}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-muted transition-colors text-muted-foreground"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Sport badge */}
              {entry.sport && entry.sport !== 'all' && (
                <span className="inline-block text-[10px] font-display font-bold uppercase tracking-widest text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {entry.sport}
                </span>
              )}

              {/* Definition */}
              <p className="font-body text-sm leading-relaxed text-foreground/85">
                {entry.definition}
              </p>

              {/* Optional diagram */}
              {entry.diagram && (
                <div className="pt-1">
                  <PlayDiagram diagram={entry.diagram} />
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/* ─── GlossaryText: drop-in replacement for plain text ─────── */

export function GlossaryText({
  text,
  sport,
}: {
  text: string;
  sport?: string;
}) {
  const [activeEntry, setActiveEntry] = useState<GlossaryEntry | null>(null);

  const regex = useMemo(() => buildGlossaryRegex(sport), [sport]);

  // Split the text into segments: plain strings and glossary terms
  const segments = useMemo(() => {
    if (!regex) return [text];
    const parts: { text: string; isGlossary: boolean }[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Reset regex state
    regex.lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), isGlossary: false });
      }
      parts.push({ text: match[0], isGlossary: true });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), isGlossary: false });
    }
    return parts;
  }, [text, regex]);

  return (
    <>
      {segments.map((seg, i) => {
        if (typeof seg === 'string') return <Fragment key={i}>{seg}</Fragment>;
        if (!seg.isGlossary) return <Fragment key={i}>{seg.text}</Fragment>;
        const entry = lookupGlossaryTerm(seg.text);
        if (!entry) return <Fragment key={i}>{seg.text}</Fragment>;
        return (
          <GlossaryTerm key={i} entry={entry} onTap={setActiveEntry}>
            {seg.text}
          </GlossaryTerm>
        );
      })}
      <GlossaryDrawer entry={activeEntry} onClose={() => setActiveEntry(null)} />
    </>
  );
}
